/**
 * Module Pane Context — the single source of truth for "which module is open".
 *
 * Before this provider, four peer writers answered that question and drifted:
 * the URL param, ChatBox-local pane state, server-persisted panel state, and
 * SidebarRail's highlight trio (pendingModuleId / focusedModuleId /
 * modulePaneClosedId) stitched together by fire-and-forget window events.
 * Every gap between them was a way for a valid sidebar click to die silently
 * (see ADR 028). This context demotes everything else:
 *
 *   - AUTHORITY: the eager mirror `paneRef` — written SYNCHRONOUSLY by every
 *     setter, readable same-tick via `getPaneState()`. The React state
 *     (`paneModuleId` / `isPaneVisible`) is the rendered projection of it and
 *     may trail by one View Transition: user-intent flips route their React
 *     write through the registered flip wrapper, and
 *     `document.startViewTransition` runs its update callback ASYNC (after
 *     old-state capture). Same-tick decision-makers (MainLayout's URL hold,
 *     openPane idempotency, ChatBox's same-value skip) MUST read
 *     `getPaneState()`, never the render snapshot.
 *   - CONFIRMATION: `renderedModuleId` — what ChatBox actually has on screen.
 *     The rail's toggle-close fires ONLY against a confirmed-open pane, so a
 *     stale belief can never swallow a click as a no-op close.
 *   - SEEDS: the URL (lazy init here + conversation-change re-seed in ChatBox's
 *     hydration runner) and server-persisted panel state (applied only through
 *     the hydration guard below).
 *
 * Intent guard: every `intent: true` write bumps a sequence — synchronously,
 * even when the React flip is deferred. A hydration read captures the sequence
 * when it starts (`beginHydration`) and applies only if no intent write
 * happened since (`isHydrationCurrent`) — a background server read can never
 * overwrite a user's click.
 *
 * Intent provenance: `targetIsIntent` records whether the CURRENT pane target
 * was set by a user/agent gesture (openPane / intent-tagged target write) or
 * by background seeding. MainLayout's URL hold requires intent provenance, so
 * a stale bookmark's URL seed never delays the redirect to a valid module.
 *
 * Deferred applies re-read the mirror when they finally run (`apply` syncs
 * React state TO `paneRef`, it does not replay captured arguments), so any
 * interleaved write between flip start and flip commit wins — last write wins
 * in mirror order. A registered flip wrapper MUST eventually invoke `apply`
 * (flipPaneWithViewTransition always does; startViewTransition runs its update
 * callback even when the transition itself is skipped).
 */

import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import type { ReactNode, SetStateAction } from 'react';
import { useParams } from 'react-router-dom';
import { useIsCompactLayout } from '@/common/hooks/useIsCompactLayout';

export interface PaneWriteOpts {
  /**
   * true  — a user/agent gesture (rail click, module.focus, pane close button):
   *         bumps the intent sequence so in-flight hydration reads are discarded.
   * false — background bookkeeping (hydration seed/apply, missing-module
   *         auto-cleanup): never outranks a user's click.
   */
  intent: boolean;
}

/** Wraps a (possibly deferred) React flip in the pane open/close animation. */
export type PaneFlipWrapper = (apply: () => void, visible: boolean) => void;

interface PaneSnapshot {
  moduleId: string | null;
  visible: boolean;
  /** Whether the current target was set by a user/agent gesture (vs background seed). */
  targetIsIntent: boolean;
}

interface ModulePaneContextValue {
  /** Rendered projection of the authority (may trail a deferred flip). */
  paneModuleId: string | null;
  /** Rendered projection of the authority (may trail a deferred flip). */
  isPaneVisible: boolean;
  /**
   * What ChatBox actually rendered (null while closed, unmounted, or still
   * resolving/attaching). `isPaneVisible && renderedModuleId === paneModuleId`
   * means the pane is confirmed on screen.
   */
  renderedModuleId: string | null;
  setPaneModuleId: (value: SetStateAction<string | null>, opts: PaneWriteOpts) => void;
  /**
   * `defer: true` routes the React flip through the registered flip wrapper
   * (the pane open/close animation). The mirror + intent sequence always
   * update synchronously regardless.
   */
  setPaneVisible: (visible: boolean, opts: PaneWriteOpts & { defer?: boolean }) => void;
  /**
   * Synchronous user-intent open: records intent + updates the authority
   * mirror immediately; the React flip animates through the registered flip
   * wrapper. Idempotent when the pane already targets the module and is
   * visible (the intent is still recorded).
   */
  openPane: (moduleId: string) => void;
  /** Capture the intent sequence at the start of a hydration read. */
  beginHydration: () => number;
  /** True iff no intent write happened since the token was issued. */
  isHydrationCurrent: (token: number) => boolean;
  /**
   * Eager (same-tick) read of the authority — reflects writes made earlier in
   * the current event handler and writes whose React flip is still riding a
   * View Transition. Decision logic reads THIS, not the render snapshot.
   */
  getPaneState: () => PaneSnapshot;
  setRenderedModuleId: (id: string | null) => void;
  registerPaneFlipWrapper: (wrapper: PaneFlipWrapper | null) => void;
}

const ModulePaneContext = createContext<ModulePaneContextValue | undefined>(undefined);

export function ModulePaneProvider({ children }: { children: ReactNode }) {
  // Seed from the URL on first mount (deep links / hard refresh render the
  // pane on the very first paint — no wait for async hydration). Mobile seeds
  // closed: the pane would cover the chat. Conversation changes re-seed via
  // ChatBox's hydration runner; this initializer never re-runs. The seed is
  // BACKGROUND provenance — a stale bookmark must not engage the URL hold.
  const { moduleId: routeModuleId } = useParams<{ moduleId?: string }>();
  const isCompact = useIsCompactLayout();
  const [paneModuleId, paneModuleIdSet] = useState<string | null>(
    () => (isCompact ? null : (routeModuleId ?? null)),
  );
  const [isPaneVisible, paneVisibleSet] = useState<boolean>(
    () => (isCompact ? false : Boolean(routeModuleId)),
  );
  const [renderedModuleId, renderedModuleIdSet] = useState<string | null>(null);

  // The eager authority mirror (the ChatDockContext dockStateRef pattern,
  // load-bearing here): every setter writes it synchronously; deferred applies
  // sync React state TO it. All same-tick reads go through getPaneState().
  const paneRef = useRef<PaneSnapshot>({
    moduleId: isCompact ? null : (routeModuleId ?? null),
    visible: isCompact ? false : Boolean(routeModuleId),
    targetIsIntent: false,
  });
  const intentSeqRef = useRef(0);
  const flipWrapperRef = useRef<PaneFlipWrapper | null>(null);

  // Sync React state to the CURRENT mirror. Deferred flips run this when the
  // View Transition's update callback fires — re-reading the mirror (instead
  // of replaying captured args) makes interleaved writes converge to the
  // latest one. React bails out on Object.is-equal values, so re-asserting an
  // already-committed value is free.
  const applyMirrorToState = useCallback(() => {
    const snapshot = paneRef.current;
    paneModuleIdSet(snapshot.moduleId);
    paneVisibleSet(snapshot.visible);
  }, []);

  const setPaneModuleId = useCallback((value: SetStateAction<string | null>, opts: PaneWriteOpts) => {
    if (opts.intent) intentSeqRef.current += 1;
    // Resolve functional updates against the eager mirror (every write goes
    // through these setters, so the mirror is always current) — keeps the
    // updater out of React's setState so StrictMode double-invocation can't
    // double-apply it.
    const next = typeof value === 'function'
      ? (value as (cur: string | null) => string | null)(paneRef.current.moduleId)
      : value;
    if (next === paneRef.current.moduleId) {
      // Same target — intent recorded above; skip the React write so a write
      // landing between a deferred flip's start and its commit can't flash
      // the new content into the transition's OLD snapshot (which kills the
      // morph). The pending apply (or the already-committed state) carries it.
      paneRef.current = { ...paneRef.current, targetIsIntent: paneRef.current.targetIsIntent || opts.intent };
      return;
    }
    paneRef.current = { ...paneRef.current, moduleId: next, targetIsIntent: opts.intent };
    paneModuleIdSet(next);
  }, []);

  const setPaneVisible = useCallback((visible: boolean, opts: PaneWriteOpts & { defer?: boolean }) => {
    if (opts.intent) intentSeqRef.current += 1;
    if (paneRef.current.visible === visible) return; // intent recorded; nothing to flip
    paneRef.current = { ...paneRef.current, visible };
    const wrapper = opts.defer ? flipWrapperRef.current : null;
    if (wrapper) wrapper(applyMirrorToState, visible);
    else paneVisibleSet(visible);
  }, [applyMirrorToState]);

  const openPane = useCallback((moduleId: string) => {
    intentSeqRef.current += 1;
    const current = paneRef.current;
    if (current.moduleId === moduleId && current.visible) {
      // Already targeting this module and visible — the intent bump above
      // still invalidates in-flight hydration, but there is nothing to flip
      // (and no animation to run).
      paneRef.current = { ...current, targetIsIntent: true };
      return;
    }
    // AUTHORITY WRITE IS SYNCHRONOUS — the mirror flips now, so MainLayout's
    // URL hold and any same-tick reader see the click immediately. Only the
    // React render flip rides the (async) View Transition.
    paneRef.current = { moduleId, visible: true, targetIsIntent: true };
    const wrapper = flipWrapperRef.current;
    if (wrapper) wrapper(applyMirrorToState, true);
    else applyMirrorToState();
  }, [applyMirrorToState]);

  const beginHydration = useCallback(() => intentSeqRef.current, []);
  const isHydrationCurrent = useCallback((token: number) => token === intentSeqRef.current, []);
  const getPaneState = useCallback(() => ({ ...paneRef.current }), []);

  const setRenderedModuleId = useCallback((id: string | null) => {
    renderedModuleIdSet(id);
  }, []);

  const registerPaneFlipWrapper = useCallback((wrapper: PaneFlipWrapper | null) => {
    flipWrapperRef.current = wrapper;
  }, []);

  const value = useMemo<ModulePaneContextValue>(
    () => ({
      paneModuleId,
      isPaneVisible,
      renderedModuleId,
      setPaneModuleId,
      setPaneVisible,
      openPane,
      beginHydration,
      isHydrationCurrent,
      getPaneState,
      setRenderedModuleId,
      registerPaneFlipWrapper,
    }),
    [
      paneModuleId,
      isPaneVisible,
      renderedModuleId,
      setPaneModuleId,
      setPaneVisible,
      openPane,
      beginHydration,
      isHydrationCurrent,
      getPaneState,
      setRenderedModuleId,
      registerPaneFlipWrapper,
    ],
  );

  return <ModulePaneContext.Provider value={value}>{children}</ModulePaneContext.Provider>;
}

export function useModulePane() {
  const context = useContext(ModulePaneContext);
  if (!context) {
    throw new Error('useModulePane must be used within a ModulePaneProvider');
  }
  return context;
}
